import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { PublicLayout } from "./components/PublicLayout";
import { RequireAuth } from "./components/RequireAuth";
import { LoginPage } from "./pages/LoginPage";
import { CallbackPage } from "./pages/CallbackPage";
import { SlackCallbackPage } from "./pages/SlackCallbackPage";
import { GoogleCallbackPage } from "./pages/GoogleCallbackPage";
import { GuidePage } from "./pages/GuidePage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { TermsPage } from "./pages/TermsPage";
import { SupportPage } from "./pages/SupportPage";
// Authenticated app — MUI shell only (kt AppShell retired).
import { MuiAppShell } from "./mui/MuiAppShell";
import { StandupMuiPage } from "./pages/dashboard/StandupMuiPage";
import { RunStandupPage } from "./pages/standup/RunStandupPage";
import { FinancePageMui } from "./pages/mui/FinancePageMui";
import { PricingSheetPageMui } from "./pages/mui/PricingSheetPageMui";
import { CustomerListPageMui } from "./pages/mui/CustomerListPageMui";
import { CustomerKnowledgePageMui } from "./pages/mui/CustomerKnowledgePageMui";
import { TodayPageMui } from "./pages/mui/TodayPageMui";
import { BrainHealthPageMui } from "./pages/mui/BrainHealthPageMui";
import { AeronetKnowledgePageMui } from "./pages/mui/AeronetKnowledgePageMui";
import { PersonPage } from "./pages/mui/people/PersonPage";
import { ProjectsPageMui } from "./pages/mui/ProjectsPageMui";
import { ProjectDetailPageMui } from "./pages/mui/ProjectDetailPageMui";
import { StandupHistoryPageMui } from "./pages/mui/StandupHistoryPageMui";
import { SettingsConnectPageMui } from "./pages/mui/SettingsConnectPageMui";
import { CockpitPageMui } from "./pages/mui/CockpitPageMui";
import { DeskPageMui } from "./pages/mui/DeskPageMui";
import { AdminOverviewPageMui } from "./pages/mui/AdminOverviewPageMui";
import { AdminIntegrationsPageMui } from "./pages/mui/AdminIntegrationsPageMui";
import { IntegrationTemplatesPageMui } from "./pages/mui/IntegrationTemplatesPageMui";
import { AdminAuditPageMui } from "./pages/mui/AdminAuditPageMui";
import { AdminUsersPageMui } from "./pages/mui/AdminUsersPageMui";
import { FeedPageMui } from "./pages/mui/FeedPageMui";
import { AgentsPageMui } from "./pages/mui/AgentsPageMui";
import { AtlasPageMui } from "./pages/mui/AtlasPageMui";
import { PeoplePageMui } from "./pages/mui/PeoplePageMui";
import { SourcesPageMui } from "./pages/mui/SourcesPageMui";
import { HealthPageMui } from "./pages/mui/HealthPageMui";
import { MyPageMui } from "./pages/mui/MyPageMui";

/** Param-aware redirect for retired -mui URLs (Navigate does not interpolate :params). */
function RedirectTo({ to }: { to: (params: Record<string, string | undefined>) => string }) {
  const params = useParams();
  return <Navigate to={to(params)} replace />;
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Standalone surfaces — no shell chrome (auth + OAuth redirects). */}
        <Route path="/" element={<Navigate to="/today" replace />} />
        <Route path="/home" element={<Navigate to="/today" replace />} />
        <Route path="/dashboard" element={<Navigate to="/today" replace />} />
        <Route path="/signup" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/callback" element={<CallbackPage />} />
        <Route path="/slack/callback" element={<SlackCallbackPage />} />
        <Route path="/google/callback" element={<GoogleCallbackPage />} />

        <Route element={<RequireAuth />}>
          {/* Printable pricing sheet — outside the shell (page is the document). */}
          <Route path="/customers/:id/pricing-sheet" element={<PricingSheetPageMui />} />
          {/* Run-standup walkthrough — full-screen takeover, no shell chrome. */}
          <Route path="/standup/run" element={<RunStandupPage />} />

          {/* Sole authenticated chrome. */}
          <Route element={<MuiAppShell />}>
            <Route path="/today" element={<TodayPageMui />} />
            <Route path="/brain-health" element={<BrainHealthPageMui />} />
            <Route path="/standup" element={<StandupMuiPage />} />
            <Route path="/standup/history" element={<StandupHistoryPageMui />} />
            <Route path="/standup/history/:date" element={<StandupHistoryPageMui />} />
            <Route path="/aeronet" element={<AeronetKnowledgePageMui />} />
            <Route path="/people/:slug" element={<PersonPage />} />
            <Route path="/settings" element={<SettingsConnectPageMui />} />
            <Route path="/atlas" element={<AtlasPageMui />} />
            <Route path="/cockpit" element={<CockpitPageMui />} />
            <Route path="/desk" element={<DeskPageMui />} />
            <Route path="/customers" element={<CustomerListPageMui />} />
            <Route path="/customers/:customerId" element={<CustomerKnowledgePageMui />} />
            <Route path="/projects" element={<ProjectsPageMui />} />
            <Route path="/projects/:id" element={<ProjectDetailPageMui />} />
            <Route path="/finance" element={<FinancePageMui />} />
            <Route path="/my" element={<MyPageMui />} />
            <Route path="/feed" element={<Navigate to="/feed-bus" replace />} />
            <Route path="/feed-bus" element={<FeedPageMui />} />
            <Route path="/agents" element={<AgentsPageMui />} />

            {/* Folded / legacy aliases → canonical. */}
            <Route path="/review" element={<Navigate to="/cockpit" replace />} />
            <Route path="/questions" element={<Navigate to="/standup#questions" replace />} />
            <Route path="/weather" element={<Navigate to="/brain-health" replace />} />
            <Route path="/insights" element={<Navigate to="/brain-health" replace />} />
            <Route path="/activity" element={<Navigate to="/brain-health" replace />} />
            <Route path="/internal" element={<Navigate to="/aeronet" replace />} />
            <Route path="/search" element={<Navigate to="/today" replace />} />
            <Route path="/settings-legacy" element={<Navigate to="/settings" replace />} />

            {/* Former -mui parallel routes → canonical (bookmarks / old links). */}
            <Route path="/today-mui" element={<Navigate to="/today" replace />} />
            <Route path="/brain-health-mui" element={<Navigate to="/brain-health" replace />} />
            <Route path="/standup-mui" element={<Navigate to="/standup" replace />} />
            <Route path="/standup-history-mui" element={<Navigate to="/standup/history" replace />} />
            <Route path="/standup-history-mui/:date" element={<RedirectTo to={(p) => `/standup/history/${p.date}`} />} />
            <Route path="/aeronet-mui" element={<Navigate to="/aeronet" replace />} />
            <Route path="/settings-mui" element={<Navigate to="/settings" replace />} />
            <Route path="/atlas-mui" element={<Navigate to="/atlas" replace />} />
            <Route path="/cockpit-mui" element={<Navigate to="/cockpit" replace />} />
            <Route path="/customers-mui" element={<Navigate to="/customers" replace />} />
            <Route path="/customers-mui/:customerId" element={<RedirectTo to={(p) => `/customers/${p.customerId}`} />} />
            <Route path="/projects-mui" element={<Navigate to="/projects" replace />} />
            <Route path="/projects-mui/:id" element={<RedirectTo to={(p) => `/projects/${p.id}`} />} />
            <Route path="/finance-mui" element={<Navigate to="/finance" replace />} />
            <Route path="/my-mui" element={<Navigate to="/my" replace />} />
            <Route path="/feed-mui" element={<Navigate to="/feed-bus" replace />} />
            <Route path="/feed-bus-mui" element={<Navigate to="/feed-bus" replace />} />
            <Route path="/agents-mui" element={<Navigate to="/agents" replace />} />
            <Route path="/admin-mui" element={<Navigate to="/admin" replace />} />
            <Route path="/admin-mui/integrations" element={<Navigate to="/admin/integrations" replace />} />
            <Route path="/admin-mui/audit" element={<Navigate to="/admin/audit" replace />} />
            <Route path="/admin-mui/users" element={<Navigate to="/admin/users" replace />} />
            <Route path="/people-mui" element={<Navigate to="/people" replace />} />
            <Route path="/sources-mui" element={<Navigate to="/sources" replace />} />
            <Route path="/health-mui" element={<Navigate to="/health" replace />} />

            <Route element={<RequireAuth requireAdmin />}>
              <Route path="/admin" element={<AdminOverviewPageMui />} />
              <Route path="/admin/integrations" element={<AdminIntegrationsPageMui />} />
              <Route path="/admin/integration-templates" element={<IntegrationTemplatesPageMui />} />
              <Route path="/admin/audit" element={<AdminAuditPageMui />} />
              <Route path="/admin/users" element={<AdminUsersPageMui />} />
              <Route path="/people" element={<PeoplePageMui />} />
              <Route path="/sources" element={<SourcesPageMui />} />
              <Route path="/health" element={<HealthPageMui />} />
            </Route>
          </Route>
        </Route>

        {import.meta.env.DEV && (
          <Route element={<RequireAuth />}>
            <Route element={<MuiAppShell />}>
              <Route path="/dev/atlas" element={<AtlasPageMui />} />
            </Route>
          </Route>
        )}

        <Route element={<PublicLayout />}>
          <Route path="/guide" element={<GuidePage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/support" element={<SupportPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
